// When and where the sky's animated bits happen, as pure functions with a
// seedable random source (tested without a DOM). The rules come from Night
// Sky Construction: slow and rare, shooting stars at the edges and never
// while a word is in progress, and nothing moving within a playable node's
// blocking radius.
import type { QuietZone } from '../skyMotionStore';
import type { AmbientConfig } from './ambientConfig';

export interface Point {
  x: number;
  y: number;
}

/** The view in pixels, and pixels per sky unit. */
export interface View {
  width: number;
  height: number;
  scale: number;
}

export interface Segment {
  from: Point;
  to: Point;
}

/** What may happen right now. */
export interface AmbientGate {
  /** No transition under way. */
  resting: boolean;
  reduced: boolean;
  /** A word is being spelled on the board. */
  wordInProgress: boolean;
}

export function mayTwinkle(g: AmbientGate): boolean {
  return g.resting && !g.reduced;
}

export function mayShootingStar(g: AmbientGate): boolean {
  return g.resting && !g.reduced && !g.wordInProgress;
}

export function maySatellite(g: AmbientGate): boolean {
  return g.resting && !g.reduced;
}

/** Seconds to the next event: the mean, give or take jitter of it. */
export function nextDelayS(meanS: number, jitter: number, rand: () => number): number {
  return meanS * (1 - jitter + 2 * jitter * rand());
}

export function distanceToSegment(p: Point, s: Segment): number {
  const dx = s.to.x - s.from.x;
  const dy = s.to.y - s.from.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - s.from.x) * dx + (p.y - s.from.y) * dy) / len2));
  return Math.hypot(p.x - (s.from.x + t * dx), p.y - (s.from.y + t * dy));
}

export function segmentClear(s: Segment, zones: readonly QuietZone[]): boolean {
  return zones.every((z) => distanceToSegment(z, s) > z.r);
}

export function pointClear(p: Point, zones: readonly QuietZone[]): boolean {
  return zones.every((z) => Math.hypot(p.x - z.x, p.y - z.y) > z.r);
}

/**
 * A shooting star's head path in pixels: it starts in the band along the
 * left or right edge, in the upper part of the view, and runs outward and
 * down. Null when every try crosses a quiet zone.
 */
export function pickShootingStar(view: View, cfg: AmbientConfig['shootingStar'], zones: readonly QuietZone[], rand: () => number, tries = 8): Segment | null {
  for (let i = 0; i < tries; i++) {
    const left = rand() < 0.5;
    const band = cfg.edgeBand * view.width;
    const x = left ? rand() * band : view.width - rand() * band;
    const y = view.height * (0.06 + 0.5 * rand());
    const angle = ((cfg.minAngle + (cfg.maxAngle - cfg.minAngle) * rand()) * Math.PI) / 180;
    const travel = cfg.travel * view.scale;
    const s = { from: { x, y }, to: { x: x + (left ? -1 : 1) * travel * Math.cos(angle), y: y + travel * Math.sin(angle) } };
    // The tail trails behind the head along the same line.
    const tail = cfg.length * view.scale;
    const back = { x: s.from.x - (left ? -1 : 1) * tail * Math.cos(angle), y: s.from.y - tail * Math.sin(angle) };
    if (segmentClear({ from: back, to: s.to }, zones)) return s;
  }
  return null;
}

/**
 * A satellite's path in pixels: in from one side, out the other, staying in
 * the upper three quarters (clear of the horizon on home). Null when every
 * try crosses a quiet zone.
 */
export function pickSatellite(view: View, zones: readonly QuietZone[], rand: () => number, tries = 12): Segment | null {
  for (let i = 0; i < tries; i++) {
    const ltr = rand() < 0.5;
    const y0 = view.height * (0.06 + 0.6 * rand());
    const y1 = Math.min(view.height * 0.72, Math.max(view.height * 0.04, y0 + view.height * (rand() - 0.5) * 0.5));
    const margin = 12;
    const s = {
      from: { x: ltr ? -margin : view.width + margin, y: ltr ? y0 : y1 },
      to: { x: ltr ? view.width + margin : -margin, y: ltr ? y1 : y0 },
    };
    if (segmentClear(s, zones)) return s;
  }
  return null;
}

/** How many stars should be twinkling at once for this share of the stars in view. */
export function twinkleTarget(share: number, starsInView: number): number {
  return Math.round(share * starsInView);
}
